import './Alert.scss'
import type { ComponentProps, ReactNode } from 'react'
import {
  MdOutlineCheckCircle,
  MdOutlineErrorOutline,
  MdOutlineInfo,
  MdOutlineWarningAmber
} from 'react-icons/md'
import { clx } from '../clx'

export type AlertTone = 'info' | 'success' | 'warning' | 'danger'

const ICONS: Record<AlertTone, ReactNode> = {
  info: <MdOutlineInfo />,
  success: <MdOutlineCheckCircle />,
  warning: <MdOutlineWarningAmber />,
  danger: <MdOutlineErrorOutline />
}

export interface AlertProps extends Omit<ComponentProps<'div'>, 'title' | 'role'> {
  tone?: AlertTone
  title?: ReactNode
  /** End slot (a button or link). Wraps under the text on narrow screens. */
  action?: ReactNode
  /**
   * Announce it: `role="alert"` for danger, `role="status"` otherwise. Only
   * for messages that appear in response to something; a static notice
   * (e.g. the release-stage warning) stays silent. Default false.
   */
  isLive?: boolean
}

export function Alert({
  tone = 'info',
  title,
  action,
  isLive = false,
  className,
  children,
  ref,
  ...rest
}: AlertProps) {
  const hasTitle = title !== undefined && title !== null && title !== false
  return (
    <div
      ref={ref}
      {...rest}
      {...(isLive && { role: tone === 'danger' ? 'alert' : 'status' })}
      className={clx(
        'alert',
        `alert--as-${tone}`,
        hasTitle && 'alert--has-title',
        action !== undefined && 'alert--has-action',
        className
      )}
    >
      <span className="alert__icon" aria-hidden="true">
        {ICONS[tone]}
      </span>
      <div className="alert__content">
        {hasTitle && <p className="alert__title">{title}</p>}
        {children !== undefined && <div className="alert__body">{children}</div>}
      </div>
      {action !== undefined && <div className="alert__action">{action}</div>}
    </div>
  )
}
